import { useState, useEffect, useEffectEvent, useMemo } from "react";
import classNames from "classnames";
import { SegmentGroup, useBreakpoint } from "@simplybusiness/mobius";
import { PaymentSummary } from "../../../shared/PersistentSummary/PaymentSummary/PaymentSummary";
import { PaymentOptions } from "../../../shared/PersistentSummary/PaymentOptions";
import { JourneyContinuationNotice } from "../../../shared/PersistentSummary/JourneyContinuationNotice";
import { currencyFormatter } from "../../../shared/PersistentSummary/UtilsHelper";
import { CombinedPaymentContext } from "../../../shared/PersistentSummary/CombinedPaymentContext";
import { SummaryWrapper } from "../SummaryWrapper";
import type { CartUpdateResponse } from "../SummaryWrapper";
import { CallToAction } from "./CallToAction";
import {
  upfrontPayments,
  upfrontPaymentsTotal,
  monthlyPayments,
  firstMonthlyPaymentDate,
  monthlyPaymentSummary,
  productInfoTexts,
  selectProductsMarkedForPurchase,
} from "../../../shared/PersistentSummary/helpers/PersistentPaymentSummaryHelper";
import type {
  PersistentCartPaymentOptionsProps,
  ProductInformation,
} from "./types";

export const PersistentCartPaymentOptions = ({
  businessInformation,
  productsInformation,
  paymentBreakdown,
  currency,
  totalPaymentAmount,
  requestPath,
  csrfToken,
  journeyToken,
  continuationProduct = "",
  multiProductText,
  onPurchaseCartDigestUpdate = () => {},
  onPaymentOptionsUpdate = () => {},
  onCallToActionClick = () => {},
  onPayTodayAmountUpdate = () => {},
  showPaymentSummaryOnly = false,
  enableCheckoutFormSubmission = true,
  enablePaymentOptionsServerUpdate = false,
  combinedPaymentPage = false,
  isExternalCarrier = false,
  hasMonthlyFees = false,
  cardFeeDisclosureText = "",
  poweredByStripeLogo = "",
  agreementChecklist = [],
  eventTrackerCallback,
  legalAgreementButtonsNotCheckedCallback,
  checkboxLinkClickedCallback,
}: PersistentCartPaymentOptionsProps) => {
  const [productsInfo, setProductsInfo] = useState(productsInformation);
  const [dynamicPaymentBreakdown, setDynamicPaymentBreakdown] =
    useState(paymentBreakdown);
  const [totalPayment, setTotalPayment] = useState(totalPaymentAmount);
  const [isFormSubmitting, setIsFormSubmitting] = useState(false);
  const breakpointSize = useBreakpoint([
    { size: "sm", value: 639 },
    { size: "lg", value: 640 },
  ]).breakpoint.size;

  const productsMarkedForPurchase = useMemo(
    () => selectProductsMarkedForPurchase(productsInfo),
    [productsInfo],
  );

  const firePaymentOptionChangeEvent = (
    cartItemReference: string,
    newPaymentPeriod: string,
  ) => {
    const currentProduct = productsInfo.find(
      product => product.cartItemReference === cartItemReference,
    );

    window.EventTracker?.trigger(
      "payment_option_changed",
      newPaymentPeriod,
      ...(currentProduct ? currentProduct.coverNames : [""]),
    );
  };

  const fireCheckoutButtonClickedEvent = () => {
    productsInfo.forEach(product => {
      window.EventTracker?.trigger(
        "checkout_button_clicked",
        product.selectedPaymentOption,
        ...product.coverNames,
      );
    });
  };

  const handleUpdateItem = (response: CartUpdateResponse) => {
    setProductsInfo(response.productsInformation);
    setTotalPayment(response.totalPaymentAmount);
    setDynamicPaymentBreakdown(response.paymentBreakdown);
    onPaymentOptionsUpdate(response.productsInformation);
    onPurchaseCartDigestUpdate(response.purchase_cart_digest);
  };

  const handlePaymentOptionsClientSideUpdate = (
    cartItemReference: string,
    newPaymentPeriod: string,
  ) => {
    firePaymentOptionChangeEvent(cartItemReference, newPaymentPeriod);
    setProductsInfo(prevState =>
      prevState.map(productInfo =>
        productInfo.cartItemReference === cartItemReference
          ? { ...productInfo, selectedPaymentOption: newPaymentPeriod }
          : productInfo,
      ),
    );
  };

  const upfrontPaymentsTotalValue = useMemo(() => {
    return upfrontPaymentsTotal(productsMarkedForPurchase, currency);
  }, [productsMarkedForPurchase, currency]);

  const buttonText = upfrontPaymentsTotalValue
    ? `Pay ${upfrontPaymentsTotalValue}`
    : "Pay now";

  const notifyPayTodayAmount = useEffectEvent(onPayTodayAmountUpdate);

  useEffect(() => {
    notifyPayTodayAmount(buttonText);
  }, [buttonText]);

  const upfrontPaymentBreakdown = enablePaymentOptionsServerUpdate
    ? dynamicPaymentBreakdown.downpayment
    : upfrontPayments(productsMarkedForPurchase, currency);

  const monthlyPaymentBreakdown = enablePaymentOptionsServerUpdate
    ? dynamicPaymentBreakdown.installments
    : monthlyPayments(productsMarkedForPurchase, currency, hasMonthlyFees);

  return (
    <CombinedPaymentContext.Provider value={combinedPaymentPage}>
      <form
        className="mobius mobius-persistent-cart-payment-options"
        data-testid="checkout-form"
        id="checkout-form"
        {...(enableCheckoutFormSubmission
          ? {
              action: requestPath,
              method: "POST",
              onSubmit: () => setIsFormSubmitting(true),
            }
          : {})}
      >
        <input
          name="authenticity_token"
          type="hidden"
          data-testid="authenticity-token"
          value={csrfToken}
        />
        <input
          name="journey_token"
          type="hidden"
          value={journeyToken}
          data-testid="journey-token"
        />
        <div
          className={classNames(
            "mobius-persistent-cart-payment-options__grid",
            {
              "mobius-persistent-cart-payment-options--combined":
                combinedPaymentPage,
              "mobius-persistent-cart-payment-options--summary-only":
                showPaymentSummaryOnly,
            },
          )}
        >
          {!showPaymentSummaryOnly && (
            <>
              <div
                className={classNames(
                  "mobius-persistent-cart-payment-options__products",
                  {
                    "mobius-persistent-cart-payment-options__products--combined":
                      combinedPaymentPage,
                  },
                )}
              >
                {productsInfo.map((productInfo: ProductInformation) => {
                  const {
                    productSummary,
                    cartItemReference,
                    markedForPurchase,
                    promptInformation,
                    availablePaymentOptions,
                  } = productInfo;

                  return (
                    <SegmentGroup
                      key={cartItemReference}
                      className={classNames(
                        "mobius-persistent-cart-payment-options__product-group",
                        {
                          "mobius-persistent-cart-payment-options__product-group--non-purchasable":
                            promptInformation?.promptCustomer,
                        },
                      )}
                      data-testid={cartItemReference}
                      horizontal={breakpointSize === "lg"}
                    >
                      <SummaryWrapper
                        productSummary={productSummary.coverInformation}
                        policyStartDate={productSummary.policyStartDate}
                        insurerLogo={productSummary.insurerLogo}
                        hasPrimaryCover={productSummary.hasPrimaryCover}
                        hasSharedLimits={productSummary.hasSharedLimits}
                        mobileView
                        editURL={productInfo.editUrl}
                        markedForPurchase={markedForPurchase}
                        onUpdateItem={handleUpdateItem}
                        cartItemReference={cartItemReference}
                        promptInformation={promptInformation}
                        eligibleForRemoval={productInfo.eligibleForRemoval}
                        editModalDescription={productInfo.editModalDescription}
                      />
                      <PaymentOptions
                        annual={availablePaymentOptions.annual}
                        monthly={availablePaymentOptions.monthly}
                        currency={currency}
                        selectedOption={productInfo.selectedPaymentOption}
                        cartItemReference={cartItemReference}
                        fireEvent={firePaymentOptionChangeEvent}
                        markedForPurchase={markedForPurchase}
                        promptInformation={promptInformation}
                        onUpdateItem={handleUpdateItem}
                        enablePaymentOptionsServerUpdate={
                          enablePaymentOptionsServerUpdate
                        }
                        hasMonthlyFees={hasMonthlyFees}
                        onPaymentOptionsClientUpdate={
                          handlePaymentOptionsClientSideUpdate
                        }
                      />
                    </SegmentGroup>
                  );
                })}
              </div>
              {continuationProduct && (
                <JourneyContinuationNotice
                  continuationProduct={continuationProduct}
                />
              )}
              <CallToAction
                enableCheckoutFormSubmission={enableCheckoutFormSubmission}
                poweredByStripeLogo={poweredByStripeLogo}
                agreementChecklist={agreementChecklist}
                eventTrackerCallback={eventTrackerCallback}
                legalAgreementButtonsNotCheckedCallback={
                  legalAgreementButtonsNotCheckedCallback
                }
                checkboxLinkClickedCallback={checkboxLinkClickedCallback}
                isFormSubmitting={isFormSubmitting}
                fireCheckoutButtonClickedEvent={fireCheckoutButtonClickedEvent}
                onCallToActionClick={onCallToActionClick}
                buttonText={buttonText}
              />
            </>
          )}
          <div className="mobius-persistent-cart-payment-options__summary-box">
            <PaymentSummary
              primaryTrade={businessInformation.primaryTrade}
              businessName={businessInformation.businessName}
              upfrontPayments={upfrontPaymentBreakdown}
              upfrontPaymentsTotal={upfrontPaymentsTotalValue}
              monthlyPayments={monthlyPaymentBreakdown}
              monthlyPaymentSummary={monthlyPaymentSummary(
                productsMarkedForPurchase,
                currency,
              )}
              firstMonthlyPaymentDate={firstMonthlyPaymentDate(
                productsMarkedForPurchase,
                currency,
              )}
              totalPaymentAmount={currencyFormatter(currency).format(
                totalPayment,
              )}
              nonRefundableTexts={productInfoTexts(
                productsMarkedForPurchase,
                "nonRefundableText",
              )}
              surchargeTexts={productInfoTexts(
                productsMarkedForPurchase,
                "surchargeText",
              )}
              multiProductText={multiProductText ?? ""}
              cardFeeDisclosureText={cardFeeDisclosureText}
              isExternalCarrier={isExternalCarrier}
              hasMonthlyFees={hasMonthlyFees}
            />
          </div>
        </div>
      </form>
    </CombinedPaymentContext.Provider>
  );
};
